import { DOMAIN_SHORT } from "@/lib/api";
import { PASS } from "@/components/GradeChart";
import { RubricTrend } from "@/components/PerformanceExtras";

const avg = (xs) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);
const ATTRS = [["quality_safety", "Quality & safety"], ["rural_context", "Rural context"], ["structure", "Structure"]];

const Bar = ({ label, value, max, testid, sub }) => {
  const pct = Math.round((value / max) * 100);
  const color = pct >= 70 ? "var(--ok)" : pct >= PASS ? "var(--warn)" : "var(--alert)";
  return (
    <div data-testid={testid} className="grid grid-cols-[minmax(110px,170px),1fr,auto] items-center gap-3 text-sm">
      <span className="truncate">{label}</span>
      <span className="h-2 rounded-full overflow-hidden" style={{ background: "var(--muted-bg)" }}><span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></span>
      <span className="font-mono text-xs font-bold w-20 text-right">{max === 4 ? `${value.toFixed(1)}/4` : `${value}%`}{sub && <span className="font-normal" style={{ color: "var(--ink-3)" }}> {sub}</span>}</span>
    </div>
  );
};

export const analyse = (vivas, driving) => {
  const domains = DOMAIN_SHORT.map((name, i) => {
    const v = vivas.filter((a) => a.domain === i + 1).map((a) => a.overall_score);
    const d = driving.filter((a) => a.domain === i + 1 && a.score != null).map((a) => a.score * 10);
    return { n: i + 1, name, viva: avg(v), vivaN: v.length, drive: avg(d), driveN: d.length };
  });
  const attrs = ATTRS.map(([k, label]) => {
    const xs = vivas.map((a) => a.rubric?.[k]).filter((x) => typeof x === "number");
    return { k, label, value: xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null };
  }).filter((a) => a.value != null);
  const tried = domains.filter((d) => d.viva != null).sort((a, b) => b.viva - a.viva);
  const strongest = tried.filter((d) => d.viva >= PASS).slice(0, 3);
  const weakest = [...tried].reverse().filter((d) => d.viva < 70 && !strongest.includes(d)).slice(0, 3);
  return { domains, attrs, strongest, weakest, untried: domains.filter((d) => !d.vivaN && !d.driveN) };
};

export const StrengthsPanel = ({ a, recentTips, vivas }) => (
  <div className="grid lg:grid-cols-2 gap-4">
    <div className="card p-5 space-y-3" data-testid="perf-domain-bars">
      <h3 className="font-display font-semibold text-sm">Average by domain (cases)</h3>
      {a.domains.filter((d) => d.viva != null).sort((x, y) => x.viva - y.viva).map((d) => <Bar key={d.n} testid={`perf-domain-${d.n}`} label={`D${d.n} ${d.name}`} value={d.viva} max={100} sub={`×${d.vivaN}`} />)}
      {a.domains.some((d) => d.drive != null) && <>
        <h3 className="font-display font-semibold text-sm pt-3 border-t" style={{ borderColor: "var(--line)" }}>Driving mode by domain</h3>
        {a.domains.filter((d) => d.drive != null).sort((x, y) => x.drive - y.drive).map((d) => <Bar key={d.n} testid={`perf-driving-domain-${d.n}`} label={`D${d.n} ${d.name}`} value={d.drive} max={100} sub={`×${d.driveN}`} />)}
      </>}
    </div>
    <div className="space-y-4">
      <div className="card p-5 grid sm:grid-cols-2 gap-4 text-sm">
        <div data-testid="perf-strengths"><h3 className="font-display font-semibold mb-2" style={{ color: "var(--ok)" }}>Strongest areas</h3>
          {a.strongest.length ? <ul className="space-y-1">{a.strongest.map((d) => <li key={d.n}>D{d.n} {d.name} <span className="font-mono text-xs">({d.viva}%)</span></li>)}</ul> : <p style={{ color: "var(--ink-3)" }}>No domain averaging a pass yet.</p>}</div>
        <div data-testid="perf-weaknesses"><h3 className="font-display font-semibold mb-2" style={{ color: "var(--alert)" }}>Focus areas</h3>
          {a.weakest.length ? <ul className="space-y-1">{a.weakest.map((d) => <li key={d.n}>D{d.n} {d.name} <span className="font-mono text-xs">({d.viva}%)</span></li>)}</ul> : <p style={{ color: "var(--ink-3)" }}>Nothing below 70%. Nice.</p>}
          {a.untried.length > 0 && <p className="mt-2 text-xs" style={{ color: "var(--ink-3)" }}>Not yet tried: {a.untried.map((d) => `D${d.n}`).join(", ")}</p>}</div>
      </div>
      {a.attrs.length > 0 && (
        <div className="card p-5 space-y-3" data-testid="perf-rubric">
          <h3 className="font-display font-semibold text-sm">Rubric breakdown: ANZCA attributes (average, pass is 2/4)</h3>
          {a.attrs.map((x) => <Bar key={x.k} testid={`perf-attr-${x.k}`} label={x.label} value={x.value} max={4} />)}
          <RubricTrend vivas={vivas} />
        </div>
      )}
      {recentTips.length > 0 && (
        <div className="card p-5" data-testid="perf-recent-tips">
          <h3 className="font-display font-semibold text-sm mb-2">Recurring advice from your recent feedback</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm" style={{ color: "var(--ink-2)" }}>{recentTips.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}
    </div>
  </div>
);
